import * as THREE from 'three';

/* ------------------------------------------------------------------
   Kinemaattinen kapseli AABB-listaa vasten. Tarkoituksella ei fysiikka-
   moottori: FPS-hahmo haluaa ennustettavaa, viritettyä liikettä.
   Pohjautuu 32_Call_of_Zulun src/player/controller.js:ään, yleistettynä:
   näppäinsidonnat tulevat bindInput()-kutsun actions-parametrina, ei
   kovakoodattuna asekohtaisina koukkuina.

   HUOM aika-askel: update(dt) käyttää RAJATTUA reaaliaika-dt:tä (kutsuja
   vastaa `Math.min(0.05, clock.getDelta())`), EI kiinteän aika-askeleen
   accumulator-mallia jota muut pelit/-pohjat käyttävät. Tämä on
   tarkoituksellinen poikkeus: hiiren katse nykisi 60 Hz -lukituksella
   korkeamman virkistystaajuuden näytöillä. Ks. pelit/CLAUDE.md:n
   FPS-pelit-osio.
------------------------------------------------------------------ */

const RADIUS = 0.36;
const STAND_HEIGHT = 1.78;
const CROUCH_HEIGHT = 1.16;
const EYE_OFFSET = 0.14; // silmät hieman kapselin huipun alapuolella

const ACCEL_GROUND = 78;
const ACCEL_AIR = 12;
const FRICTION = 11.5;
const GRAVITY = 20.5;
const JUMP_SPEED = 6.3;
const STEP_HEIGHT = 0.42;

export const SPEEDS = {
  sprint: 6.4,
  run: 4.5,
  aim: 2.3,
  crouch: 1.9,
};

const TOUCH_JOY_DEADZONE = 0.25;   // tatin kynnys jonka jälkeen suunta lasketaan aktiiviseksi
const TOUCH_LOOK_MULT = 2.4;       // kosketusveto tarvitsee suuremman kertoimen kuin hiiren movementX/Y

export class Player {
  constructor(camera, colliders, spawn) {
    this.camera = camera;
    this.colliders = colliders;
    this.position = spawn.clone();
    this.velocity = new THREE.Vector3();
    this.onGround = false;
    this.height = STAND_HEIGHT;
    this.crouching = false;
    this.sprinting = false;
    this.yaw = 0;
    this.pitch = 0;
    this.viewYaw = 0;   // ulkopuolelta lisättävä huojunta/rekyyli
    this.viewPitch = 0;
    this.bobTime = 0;
    this.bob = new THREE.Vector2();
    this.landDip = 0;
    this.mouseDelta = new THREE.Vector2();
    this.sensitivity = 0.0022;
    this.keys = new Set();
    // Kosketuksen tila: touchActive on true vain kun peli on oikeasti käynnissä
    // (ei aloitus-/lopetusruudulla) — samalla periaatteella kuin muiden pelit/-
    // pohjien "valikossa()"-vartija, jotta gate-napin natiivi klikkaus ei kaadu
    // preventDefault():iin.
    this.touchActive = false;
  }

  /** actions: { onLockChange, onPrimaryDown/Up, onSecondaryDown/Up, onKeyDown(code), onKeyUp(code) } */
  bindInput(canvas, actions = {}) {
    this.hooks = actions;

    canvas.addEventListener('click', () => {
      if (document.pointerLockElement !== canvas) canvas.requestPointerLock();
    });

    document.addEventListener('pointerlockchange', () => {
      const locked = document.pointerLockElement === canvas;
      actions.onLockChange?.(locked);
    });

    document.addEventListener('mousemove', (e) => {
      if (document.pointerLockElement !== canvas) return;
      this.mouseDelta.x += e.movementX;
      this.mouseDelta.y += e.movementY;
    });

    document.addEventListener('mousedown', (e) => {
      if (document.pointerLockElement !== canvas) return;
      if (e.button === 0) actions.onPrimaryDown?.();
      if (e.button === 2) actions.onSecondaryDown?.();
    });
    document.addEventListener('mouseup', (e) => {
      if (e.button === 0) actions.onPrimaryUp?.();
      if (e.button === 2) actions.onSecondaryUp?.();
    });
    document.addEventListener('contextmenu', (e) => e.preventDefault());

    document.addEventListener('keydown', (e) => {
      this.keys.add(e.code);
      actions.onKeyDown?.(e.code);
      if (e.code === 'Space') e.preventDefault();
    });
    document.addEventListener('keyup', (e) => {
      this.keys.delete(e.code);
      actions.onKeyUp?.(e.code);
    });
    window.addEventListener('blur', () => this.keys.clear());
  }

  /**
   * Kosketustuki: virtuaalitatti liikkeelle (#joyBase/#joyKnob), vetokatse
   * (kaikkialla muualla ruudulla) ja yleinen TOIMI-nappi (#toimiNappi) joka
   * kutsuu samoja actions.onPrimaryDown/Up-koukkuja kuin hiiren vasen nappi.
   * Ei-kosketuslaitteella tämä ei tee mitään (elementtejä ei löydy/tarvita).
   *
   * Peli-spesifiset lisänapit (esim. työkalunvaihto) EIVÄT kuulu tähän —
   * ne lisätään pelin omaan index.html/game.js:ään ja kutsuvat samoja
   * actions-koukkuja tai pelin sisäisiä funktioita suoraan.
   */
  bindTouch(actions = {}) {
    const kosketuslaite = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (!kosketuslaite) return;
    document.body.classList.add('touch');

    const joyBase = document.getElementById('joyBase');
    const joyKnob = document.getElementById('joyKnob');
    const toimiNappi = document.getElementById('toimiNappi');

    // ---- Virtuaalitatti (kiinteä sijainti, vrt. 12_lentosimulaattorin malli) ----
    let joyId = null;
    const paivitaLiikeNappaimet = (nx, ny) => {
      const aseta = (koodi, pohja) => { if (pohja) this.keys.add(koodi); else this.keys.delete(koodi); };
      aseta('KeyD', nx > TOUCH_JOY_DEADZONE);
      aseta('KeyA', nx < -TOUCH_JOY_DEADZONE);
      aseta('KeyS', ny > TOUCH_JOY_DEADZONE);
      aseta('KeyW', ny < -TOUCH_JOY_DEADZONE);
    };
    const joyLiiku = (t) => {
      if (!joyBase) return;
      const r = joyBase.getBoundingClientRect();
      const cx = r.left + r.width / 2, cy = r.top + r.height / 2;
      let dx = t.clientX - cx, dy = t.clientY - cy;
      const max = r.width / 2, len = Math.hypot(dx, dy);
      if (len > max) { dx *= max / len; dy *= max / len; }
      if (joyKnob) joyKnob.style.transform = `translate(${dx}px,${dy}px)`;
      paivitaLiikeNappaimet(dx / max, dy / max);
    };
    if (joyBase) {
      joyBase.addEventListener('touchstart', (e) => {
        if (!this.touchActive) return;
        e.preventDefault();
        joyId = e.changedTouches[0].identifier;
        joyLiiku(e.changedTouches[0]);
      }, { passive: false });
    }

    // ---- Vetokatse: mikä tahansa muu kosketus paitsi tatti/TOIMI-nappi ----
    let lookId = null;
    let lookX = 0, lookY = 0;

    window.addEventListener('touchstart', (e) => {
      if (!this.touchActive) return;
      for (const t of e.changedTouches) {
        if (t.identifier === joyId) continue;
        if (t.target?.closest?.('#joyBase, #toimiNappi')) continue;
        if (lookId !== null) continue;
        e.preventDefault();
        lookId = t.identifier;
        lookX = t.clientX; lookY = t.clientY;
      }
    }, { passive: false });

    window.addEventListener('touchmove', (e) => {
      if (!this.touchActive) return;
      for (const t of e.changedTouches) {
        if (t.identifier === joyId) { e.preventDefault(); joyLiiku(t); continue; }
        if (t.identifier !== lookId) continue;
        e.preventDefault();
        this.mouseDelta.x += (t.clientX - lookX) * TOUCH_LOOK_MULT;
        this.mouseDelta.y += (t.clientY - lookY) * TOUCH_LOOK_MULT;
        lookX = t.clientX; lookY = t.clientY;
      }
    }, { passive: false });

    const touchLoppu = (e) => {
      for (const t of e.changedTouches) {
        if (t.identifier === joyId) {
          joyId = null;
          if (joyKnob) joyKnob.style.transform = '';
          paivitaLiikeNappaimet(0, 0);
        }
        if (t.identifier === lookId) lookId = null;
      }
    };
    window.addEventListener('touchend', touchLoppu);
    window.addEventListener('touchcancel', touchLoppu);

    // ---- TOIMI-nappi: samat koukut kuin hiiren vasen nappi ----
    if (toimiNappi) {
      toimiNappi.addEventListener('touchstart', (e) => {
        if (!this.touchActive) return;
        e.preventDefault(); e.stopPropagation();
        toimiNappi.classList.add('aktiivinen');
        actions.onPrimaryDown?.();
      }, { passive: false });
      const toimiLoppu = (e) => {
        e.preventDefault(); e.stopPropagation();
        toimiNappi.classList.remove('aktiivinen');
        actions.onPrimaryUp?.();
      };
      toimiNappi.addEventListener('touchend', toimiLoppu);
      toimiNappi.addEventListener('touchcancel', toimiLoppu);
    }
  }

  /** Työntää kapselin pois jokaisesta päällekkäisestä laatikosta, akseli kerrallaan. */
  _collide(delta) {
    const half = this.height / 2;
    let grounded = false;

    this.position.y += delta.y;
    for (const box of this.colliders) {
      if (!this._overlaps(box, half)) continue;
      const centreY = this.position.y;
      if (delta.y <= 0) {
        this.position.y = box.max.y + half;
        this.velocity.y = 0;
        grounded = true;
      } else {
        this.position.y = box.min.y - half;
        this.velocity.y = 0;
      }
      if (!Number.isFinite(this.position.y)) this.position.y = centreY;
    }

    for (const axis of ['x', 'z']) {
      const before = this.position[axis];
      this.position[axis] += delta[axis];
      for (const box of this.colliders) {
        if (!this._overlaps(box, half)) continue;
        const stepTo = box.max.y + half;
        const rise = stepTo - this.position.y;
        if (rise > 0 && rise <= STEP_HEIGHT && this._clearAt(stepTo, half)) {
          this.position.y = stepTo;
          grounded = true;
          continue;
        }
        this.position[axis] = before;
        this.velocity[axis] = 0;
        break;
      }
    }

    this.onGround = grounded || this.position.y <= half + 0.001;
    if (this.position.y < half) {
      this.position.y = half;
      this.velocity.y = 0;
      this.onGround = true;
    }
  }

  _overlaps(box, half) {
    const p = this.position;
    return (
      p.x + RADIUS > box.min.x && p.x - RADIUS < box.max.x &&
      p.z + RADIUS > box.min.z && p.z - RADIUS < box.max.z &&
      p.y + half > box.min.y && p.y - half < box.max.y
    );
  }

  _clearAt(y, half) {
    const saved = this.position.y;
    this.position.y = y;
    let clear = true;
    for (const box of this.colliders) {
      if (this._overlaps(box, half)) { clear = false; break; }
    }
    this.position.y = saved;
    return clear;
  }

  update(dt, { aiming = false } = {}) {
    const sens = this.sensitivity * (aiming ? 0.62 : 1);
    this.yaw -= this.mouseDelta.x * sens;
    this.pitch -= this.mouseDelta.y * sens;
    this.pitch = THREE.MathUtils.clamp(this.pitch, -1.52, 1.52);
    const lookSpeed = this.mouseDelta.length();
    this.mouseDelta.set(0, 0);

    const k = this.keys;
    const wish = new THREE.Vector3(
      (k.has('KeyD') ? 1 : 0) - (k.has('KeyA') ? 1 : 0),
      0,
      (k.has('KeyS') ? 1 : 0) - (k.has('KeyW') ? 1 : 0),
    );
    const moving = wish.lengthSq() > 0;
    if (moving) wish.normalize().applyAxisAngle(new THREE.Vector3(0, 1, 0), this.yaw);

    this.crouching = k.has('ControlLeft') || k.has('KeyC');
    this.sprinting = (k.has('ShiftLeft') || k.has('ShiftRight')) && moving && !aiming
      && !this.crouching && !k.has('KeyS');

    const targetHeight = this.crouching ? CROUCH_HEIGHT : STAND_HEIGHT;
    if (this.height !== targetHeight) {
      const next = THREE.MathUtils.damp(this.height, targetHeight, 14, dt);
      if (next > this.height && !this._clearAt(this.position.y + (next - this.height) / 2, next / 2)) {
        // este yläpuolella, pysy kyykyssä
      } else {
        this.position.y += (next - this.height) / 2;
        this.height = next;
      }
    }

    let maxSpeed = SPEEDS.run;
    if (this.crouching) maxSpeed = SPEEDS.crouch;
    else if (aiming) maxSpeed = SPEEDS.aim;
    else if (this.sprinting) maxSpeed = SPEEDS.sprint;

    const accel = this.onGround ? ACCEL_GROUND : ACCEL_AIR;
    const horiz = new THREE.Vector3(this.velocity.x, 0, this.velocity.z);

    if (moving) {
      horiz.addScaledVector(wish, accel * dt);
      if (horiz.length() > maxSpeed) horiz.setLength(maxSpeed);
    } else if (this.onGround) {
      const drop = FRICTION * dt;
      const len = horiz.length();
      horiz.setLength(Math.max(0, len - Math.max(len, 1.2) * drop));
    }
    this.velocity.x = horiz.x;
    this.velocity.z = horiz.z;

    const wasAir = !this.onGround;
    const fallSpeed = this.velocity.y;

    if (k.has('Space') && this.onGround) {
      this.velocity.y = JUMP_SPEED;
      this.onGround = false;
    }
    this.velocity.y -= GRAVITY * dt;

    const delta = this.velocity.clone().multiplyScalar(dt);
    this._collide(delta);

    if (wasAir && this.onGround && fallSpeed < -3.5) {
      this.landDip = Math.min(0.26, -fallSpeed * 0.022);
    }
    this.landDip = THREE.MathUtils.damp(this.landDip, 0, 9, dt);

    const speed = horiz.length();
    const bobAmount = aiming ? 0.18 : 1;
    this.bobTime += dt * (6.2 + speed * 1.45);
    const strength = THREE.MathUtils.clamp(speed / SPEEDS.run, 0, 1.35) * bobAmount;
    this.bob.set(
      Math.cos(this.bobTime * 0.5) * 0.026 * strength,
      Math.sin(this.bobTime) * 0.018 * strength,
    );

    const eyeY = this.position.y + this.height / 2 - EYE_OFFSET;
    this.camera.position.set(
      this.position.x + this.bob.x * 0.6,
      eyeY + this.bob.y - this.landDip,
      this.position.z,
    );
    this.camera.rotation.set(0, 0, 0);
    this.camera.rotateY(this.yaw + this.viewYaw);
    this.camera.rotateX(this.pitch + this.viewPitch);
    const strafe = wish.lengthSq() > 0
      ? new THREE.Vector3(this.velocity.x, 0, this.velocity.z)
        .applyAxisAngle(new THREE.Vector3(0, 1, 0), -this.yaw).x / SPEEDS.run
      : 0;
    this.camera.rotateZ(-strafe * 0.018 + this.bob.x * 0.5);

    return { speed, moving, lookSpeed };
  }
}
